import { CURRENCY_DATA, Network } from ':fat/core/networks';

/**
 * Formats a value in the given network's currency
 * @param {Network} network - The network to format the value for
 * @param {string} value - The value to format
 * @returns {string} A string representing the value in the format "0.00000000 BTC"
 */
export function formatCrypto(network: Network, value: bigint) {
  const { decimals, symbol } = CURRENCY_DATA[network];

  if (value === 0n) {
    return `0 ${symbol}`;
  }

  const digits = [...value.toString()];
  const len = digits.length;

  if (len > decimals) {
    digits.splice(len - decimals, 0, '.');
  } else if (len < decimals) {
    digits.splice(0, 0, '0.', '0'.repeat(decimals - len));
  } else {
    digits.splice(0, 0, '0.');
  }

  return `${digits.join('').replace(/0+$/, '')} ${symbol}`;
}

export function formatFiat(value: number) {
  const cents = Math.round(value * 100);
  return `$${(cents / 100).toLocaleString('en', { minimumFractionDigits: 2 })}`;
}

/**
 * Formats a value in the given network's currency, truncating the fractional part to 5 decimal places
 * @param {Network} network - The network to format the value for
 * @param {bigint} value - The value to format
 * @returns {string} A string representing the value in the format "0.00000 BTC"
 */
export function formatCryptoTruncated(network: Network, value: bigint) {
  const { symbol } = CURRENCY_DATA[network];

  if (value === 0n) {
    return `0 ${symbol}`;
  }

  const fullFormat = formatCrypto(network, value);
  const [amount, unit] = fullFormat.split(' ');

  const [integerPart, fractionalPart = ''] = amount.split('.');

  // Combine integer part with up to 5 decimal places
  const truncated = `${integerPart}.${fractionalPart.slice(0, 5)}`.replace(/\.?0+$/, '');
  return `${truncated} ${unit}`;
}
